// Releases page JavaScript

import {
    apiRequest,
    initAuthenticatedPage,
    renderMarkdown,
    getDefaultProjectId,
    setDefaultProjectId,
} from "./common";

// Types
interface Project {
    id: number;
    name: string;
    key: string;
}

interface FixVersion {
    id: number;
    project_id: number;
    version_number: string;
    release_date: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

// State
let currentProjectId: number | null = null;
let currentReleaseId: number | null = null;
let releases: FixVersion[] = [];
let isCreating = false;
let hasUnsavedChanges = false;
let pendingAction: (() => void) | null = null;

// DOM elements
let projectSelect: HTMLSelectElement;
let releasesList: HTMLElement;
let releasesViewer: HTMLElement;
let newReleaseBtn: HTMLButtonElement;
let deleteReleaseDialog: HTMLDialogElement;
let deleteReleaseName: HTMLElement;
let deleteReleaseConfirmBtn: HTMLButtonElement;
let deleteReleaseCancelBtn: HTMLButtonElement;
let unsavedDialog: HTMLDialogElement;
let unsavedStayBtn: HTMLButtonElement;
let unsavedLeaveBtn: HTMLButtonElement;

function escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

function formatDate(dateString: string | null): string {
    if (!dateString) return "Not released";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function formatTimestamp(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// Initialize
async function initReleasesPage(): Promise<void> {
    // Wait for authentication to complete before making API requests
    const user = await initAuthenticatedPage();
    if (!user) {
        return;
    }

    // Get DOM elements
    projectSelect = document.getElementById("project-select") as HTMLSelectElement;
    releasesList = document.getElementById("releases-list") as HTMLElement;
    releasesViewer = document.getElementById("releases-viewer") as HTMLElement;
    newReleaseBtn = document.getElementById("new-release-btn") as HTMLButtonElement;
    deleteReleaseDialog = document.getElementById("delete-release-dialog") as HTMLDialogElement;
    deleteReleaseName = document.getElementById("delete-release-name") as HTMLElement;
    deleteReleaseConfirmBtn = document.getElementById("delete-release-confirm-btn") as HTMLButtonElement;
    deleteReleaseCancelBtn = document.getElementById("delete-release-cancel-btn") as HTMLButtonElement;
    unsavedDialog = document.getElementById("unsaved-dialog") as HTMLDialogElement;
    unsavedStayBtn = document.getElementById("unsaved-stay-btn") as HTMLButtonElement;
    unsavedLeaveBtn = document.getElementById("unsaved-leave-btn") as HTMLButtonElement;

    // Setup event listeners
    setupEventListeners();

    // Load projects
    loadProjects();
}

function setupEventListeners(): void {
    // Project selection change
    projectSelect.addEventListener("change", () => {
        const projectId = projectSelect.value ? parseInt(projectSelect.value, 10) : null;
        if (hasUnsavedChanges) {
            pendingAction = () => onProjectChange(projectId);
            unsavedDialog.showModal();
        } else {
            onProjectChange(projectId);
        }
    });

    // New release button
    newReleaseBtn.addEventListener("click", () => {
        if (hasUnsavedChanges) {
            pendingAction = () => startCreateRelease();
            unsavedDialog.showModal();
        } else {
            startCreateRelease();
        }
    });

    // Delete dialog buttons
    deleteReleaseCancelBtn.addEventListener("click", () => {
        deleteReleaseDialog.close();
    });

    deleteReleaseConfirmBtn.addEventListener("click", async () => {
        await confirmDeleteRelease();
    });

    // Delete dialog backdrop click
    deleteReleaseDialog.addEventListener("click", (e) => {
        if (e.target === deleteReleaseDialog) {
            deleteReleaseDialog.close();
        }
    });

    // Unsaved changes dialog buttons
    unsavedStayBtn.addEventListener("click", () => {
        unsavedDialog.close();
        pendingAction = null;
    });

    unsavedLeaveBtn.addEventListener("click", () => {
        unsavedDialog.close();
        hasUnsavedChanges = false;
        if (pendingAction) {
            pendingAction();
            pendingAction = null;
        }
    });

    // Unsaved dialog backdrop click
    unsavedDialog.addEventListener("click", (e) => {
        if (e.target === unsavedDialog) {
            unsavedDialog.close();
            pendingAction = null;
        }
    });
}

async function loadProjects(): Promise<void> {
    try {
        const projects = await apiRequest<Project[]>("/projects");

        // Populate project dropdown
        projectSelect.innerHTML = '<option value="">Select a project...</option>';
        projects.forEach((p) => {
            const option = document.createElement("option");
            option.value = p.id.toString();
            option.textContent = `${p.name} (${p.key})`;
            projectSelect.appendChild(option);
        });

        // Check URL params first, then default project
        const params = new URLSearchParams(window.location.search);
        const urlProjectId = params.get("projectId");

        if (urlProjectId) {
            projectSelect.value = urlProjectId;
            onProjectChange(parseInt(urlProjectId, 10));
        } else {
            const defaultProjectId = getDefaultProjectId();
            if (defaultProjectId) {
                projectSelect.value = defaultProjectId.toString();
                onProjectChange(defaultProjectId);
            }
        }
    } catch (error) {
        console.error("Failed to load projects:", error);
        releasesList.innerHTML = '<p class="error">Failed to load projects</p>';
    }
}

function onProjectChange(projectId: number | null): void {
    currentProjectId = projectId;
    currentReleaseId = null;
    isCreating = false;

    if (projectId) {
        setDefaultProjectId(projectId);
        newReleaseBtn.disabled = false;
        loadReleases();
    } else {
        newReleaseBtn.disabled = true;
        releasesList.innerHTML = '<p class="muted">Select a project to view releases</p>';
        releasesViewer.innerHTML = '<div class="empty-state"><p>Select a release to view details or create a new one</p></div>';
    }
}

async function loadReleases(): Promise<void> {
    if (!currentProjectId) return;

    releasesList.innerHTML = '<p class="loading">Loading releases...</p>';

    try {
        releases = await apiRequest<FixVersion[]>(`/projects/${currentProjectId}/fix-versions`);
        renderReleasesList();

        // Check URL for release ID
        const params = new URLSearchParams(window.location.search);
        const urlReleaseId = params.get("releaseId");
        if (urlReleaseId) {
            const releaseId = parseInt(urlReleaseId, 10);
            if (releases.some((r) => r.id === releaseId)) {
                selectRelease(releaseId);
            }
        }
    } catch (error) {
        console.error("Failed to load releases:", error);
        releasesList.innerHTML = '<p class="error">Failed to load releases</p>';
    }
}

function renderReleasesList(): void {
    if (releases.length === 0) {
        releasesList.innerHTML = '<p class="muted">No releases yet. Click "New Release" to create one.</p>';
        releasesViewer.innerHTML = '<div class="empty-state"><p>Create your first release to get started</p></div>';
        return;
    }

    releasesList.innerHTML = releases
        .map(
            (r) => `
            <div class="release-item ${r.id === currentReleaseId ? "selected" : ""}" data-id="${r.id}">
                <div class="release-version">${escapeHtml(r.version_number)}</div>
                <div class="release-date">${formatDate(r.release_date)}</div>
            </div>
        `
        )
        .join("");

    // Add click handlers
    releasesList.querySelectorAll(".release-item").forEach((item) => {
        item.addEventListener("click", () => {
            const id = parseInt((item as HTMLElement).dataset.id || "0", 10);
            if (hasUnsavedChanges) {
                pendingAction = () => selectRelease(id);
                unsavedDialog.showModal();
            } else {
                selectRelease(id);
            }
        });
    });
}

function selectRelease(releaseId: number): void {
    currentReleaseId = releaseId;
    isCreating = false;
    hasUnsavedChanges = false;

    // Update list selection
    releasesList.querySelectorAll(".release-item").forEach((item) => {
        item.classList.toggle("selected", parseInt((item as HTMLElement).dataset.id || "0", 10) === releaseId);
    });

    renderReleaseViewer();
}

function renderReleaseViewer(): void {
    const release = releases.find((r) => r.id === currentReleaseId);
    if (!release) {
        releasesViewer.innerHTML = '<div class="empty-state"><p>Select a release to view details</p></div>';
        return;
    }

    const notesHtml = release.notes ? renderMarkdown(release.notes) : "<em>No release notes</em>";

    releasesViewer.innerHTML = `
        <div class="release-viewer">
            <div class="release-viewer-header">
                <h2>${escapeHtml(release.version_number)}</h2>
                <div class="release-viewer-actions">
                    <button class="outline secondary" id="edit-release-btn">Edit</button>
                    <button class="outline delete-action" id="delete-release-btn">Delete</button>
                </div>
            </div>
            <div class="release-meta">
                <span class="release-meta-item">
                    <strong>Release Date:</strong> ${formatDate(release.release_date)}
                </span>
                <span class="release-meta-item">
                    <strong>Created:</strong> ${formatTimestamp(release.created_at)}
                </span>
                <span class="release-meta-item">
                    <strong>Updated:</strong> ${formatTimestamp(release.updated_at)}
                </span>
            </div>
            <div class="release-notes">
                <h3>Release Notes</h3>
                <div class="release-notes-content">${notesHtml}</div>
            </div>
        </div>
    `;

    // Add event listeners
    document.getElementById("edit-release-btn")?.addEventListener("click", () => {
        startEditRelease();
    });

    document.getElementById("delete-release-btn")?.addEventListener("click", () => {
        openDeleteDialog();
    });
}

function startCreateRelease(): void {
    currentReleaseId = null;
    isCreating = true;
    hasUnsavedChanges = false;

    // Deselect list items
    releasesList.querySelectorAll(".release-item").forEach((item) => {
        item.classList.remove("selected");
    });

    renderReleaseEditor(null);
}

function startEditRelease(): void {
    isCreating = false;
    hasUnsavedChanges = false;

    const release = releases.find((r) => r.id === currentReleaseId);
    renderReleaseEditor(release || null);
}

function renderReleaseEditor(release: FixVersion | null): void {
    const isNew = !release;

    releasesViewer.innerHTML = `
        <div class="release-editor">
            <h2>${isNew ? "New Release" : "Edit Release"}</h2>
            <form id="release-form" class="release-form">
                <div class="form-row">
                    <label for="version-number">
                        Version Number
                        <input type="text" id="version-number" name="version_number" required
                            placeholder="e.g., 1.0.0" value="${release ? escapeHtml(release.version_number) : ""}">
                    </label>
                </div>
                <div class="form-row">
                    <label for="release-date">
                        Release Date (optional)
                        <input type="date" id="release-date" name="release_date"
                            value="${release?.release_date || ""}">
                    </label>
                </div>
                <div class="form-row">
                    <label>
                        Release Notes (Markdown)
                        <div class="editor-tabs">
                            <button type="button" class="tab active" data-tab="edit">Edit</button>
                            <button type="button" class="tab" data-tab="preview">Preview</button>
                        </div>
                        <textarea id="notes-content" name="notes" rows="12"
                            placeholder="Write release notes here...">${release?.notes || ""}</textarea>
                        <div id="notes-preview" class="markdown-preview" style="display: none;"></div>
                    </label>
                </div>
                <div id="form-error" class="error" style="display: none;"></div>
                <div class="editor-actions">
                    <button type="button" id="cancel-btn" class="secondary">Cancel</button>
                    <button type="submit" id="save-btn">${isNew ? "Create" : "Save"}</button>
                </div>
            </form>
        </div>
    `;

    // Setup form event listeners
    const form = document.getElementById("release-form") as HTMLFormElement;
    const versionInput = document.getElementById("version-number") as HTMLInputElement;
    const dateInput = document.getElementById("release-date") as HTMLInputElement;
    const notesInput = document.getElementById("notes-content") as HTMLTextAreaElement;
    const notesPreview = document.getElementById("notes-preview") as HTMLElement;
    const cancelBtn = document.getElementById("cancel-btn") as HTMLButtonElement;

    // Track changes
    const trackChange = () => {
        hasUnsavedChanges = true;
    };
    versionInput.addEventListener("input", trackChange);
    dateInput.addEventListener("input", trackChange);
    notesInput.addEventListener("input", trackChange);

    // Form submission
    form.addEventListener("submit", async (e) => {
        e.preventDefault();
        await saveRelease();
    });

    // Cancel button
    cancelBtn.addEventListener("click", () => {
        if (hasUnsavedChanges) {
            pendingAction = () => cancelEdit();
            unsavedDialog.showModal();
        } else {
            cancelEdit();
        }
    });

    // Editor tabs
    document.querySelectorAll(".editor-tabs .tab").forEach((tab) => {
        tab.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            const tabType = target.dataset.tab;
            switchEditorTab(tabType === "preview" ? "preview" : "edit", notesInput, notesPreview);
        });
    });
}

function switchEditorTab(tab: "edit" | "preview", textarea: HTMLTextAreaElement, preview: HTMLElement): void {
    const tabs = document.querySelectorAll(".editor-tabs .tab");
    tabs.forEach((t) => {
        const tabEl = t as HTMLElement;
        tabEl.classList.toggle("active", tabEl.dataset.tab === tab);
    });

    if (tab === "preview") {
        textarea.style.display = "none";
        preview.style.display = "block";
        preview.innerHTML = renderMarkdown(textarea.value || "*No content*");
    } else {
        textarea.style.display = "block";
        preview.style.display = "none";
    }
}

function cancelEdit(): void {
    hasUnsavedChanges = false;
    isCreating = false;

    if (currentReleaseId) {
        renderReleaseViewer();
    } else {
        releasesViewer.innerHTML = '<div class="empty-state"><p>Select a release to view details or create a new one</p></div>';
    }
}

async function saveRelease(): Promise<void> {
    if (!currentProjectId) return;

    const versionInput = document.getElementById("version-number") as HTMLInputElement;
    const dateInput = document.getElementById("release-date") as HTMLInputElement;
    const notesInput = document.getElementById("notes-content") as HTMLTextAreaElement;
    const formError = document.getElementById("form-error") as HTMLElement;
    const saveBtn = document.getElementById("save-btn") as HTMLButtonElement;

    const versionNumber = versionInput.value.trim();
    const releaseDate = dateInput.value || null;
    const notes = notesInput.value.trim() || null;

    if (!versionNumber) {
        formError.textContent = "Version number is required";
        formError.style.display = "block";
        return;
    }

    formError.style.display = "none";
    saveBtn.setAttribute("aria-busy", "true");
    saveBtn.disabled = true;

    try {
        if (isCreating) {
            // Create new release
            const newRelease = await apiRequest<FixVersion>(`/projects/${currentProjectId}/fix-versions`, {
                method: "POST",
                body: JSON.stringify({
                    version_number: versionNumber,
                    release_date: releaseDate,
                    notes: notes,
                }),
            });

            releases.push(newRelease);
            releases.sort((a, b) => a.version_number.localeCompare(b.version_number));
            currentReleaseId = newRelease.id;
        } else if (currentReleaseId) {
            // Update existing release
            const existingRelease = releases.find((r) => r.id === currentReleaseId);
            const updateData: Record<string, unknown> = {};

            if (versionNumber !== existingRelease?.version_number) {
                updateData.version_number = versionNumber;
            }

            if (releaseDate !== existingRelease?.release_date) {
                if (releaseDate === null && existingRelease?.release_date) {
                    updateData.clear_release_date = true;
                } else {
                    updateData.release_date = releaseDate;
                }
            }

            if (notes !== existingRelease?.notes) {
                if (notes === null && existingRelease?.notes) {
                    updateData.clear_notes = true;
                } else {
                    updateData.notes = notes;
                }
            }

            const updatedRelease = await apiRequest<FixVersion>(
                `/projects/${currentProjectId}/fix-versions/${currentReleaseId}`,
                {
                    method: "PUT",
                    body: JSON.stringify(updateData),
                }
            );

            // Update in list
            const index = releases.findIndex((r) => r.id === currentReleaseId);
            if (index !== -1) {
                releases[index] = updatedRelease;
            }
            releases.sort((a, b) => a.version_number.localeCompare(b.version_number));
        }

        hasUnsavedChanges = false;
        isCreating = false;

        renderReleasesList();
        renderReleaseViewer();
    } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to save release";
        formError.textContent = message;
        formError.style.display = "block";
    } finally {
        saveBtn.removeAttribute("aria-busy");
        saveBtn.disabled = false;
    }
}

function openDeleteDialog(): void {
    const release = releases.find((r) => r.id === currentReleaseId);
    if (!release) return;

    deleteReleaseName.textContent = release.version_number;
    deleteReleaseDialog.showModal();
}

async function confirmDeleteRelease(): Promise<void> {
    if (!currentProjectId || !currentReleaseId) return;

    deleteReleaseConfirmBtn.setAttribute("aria-busy", "true");
    deleteReleaseConfirmBtn.disabled = true;

    try {
        await apiRequest(`/projects/${currentProjectId}/fix-versions/${currentReleaseId}`, {
            method: "DELETE",
        });

        // Remove from list
        releases = releases.filter((r) => r.id !== currentReleaseId);
        currentReleaseId = null;

        deleteReleaseDialog.close();
        renderReleasesList();
        releasesViewer.innerHTML = '<div class="empty-state"><p>Select a release to view details or create a new one</p></div>';
    } catch (error) {
        alert(`Failed to delete: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
        deleteReleaseConfirmBtn.removeAttribute("aria-busy");
        deleteReleaseConfirmBtn.disabled = false;
    }
}

// Start the page
initReleasesPage();
